import React, { useEffect, useState } from "react";
import { Row, Col, Form, Alert, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import {
  updateTeacherProfile,
  whoami,
  getRoles,
  getSchools,
  logTeacherUpdate,
  getCountryId,
  postLastLogin
} from "../services/APIClient";

import "./TeacherProfileForm.css";
import SimpleNavigationButton from "./SimpleNavigationButton";
import { formatAcademicYear } from "../utils/academicYear";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";
import { useRoleSelection } from "../contexts/RoleSelectionContext";

function TeacherProfileForm(props) {
  const [currentAcademicYear] = useAcademicYearOptions({ previousYears: 0 });
  const navigate = useNavigate();
  const { refresh: refreshRoleSelection } = useRoleSelection();

  let profilePageRedirect = props.profilePageRedirect;

  const [schoolid, setSchoolid] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [roleDefs, setRoleDefs] = useState([]);
  const [countryid, setCountryId] = useState(null);
  const [contactNumber, setContactNumber] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [errors, setErrors] = useState({});

  // State variables for tracking changes and modal
  const [initialValues, setInitialValues] = useState({});
  const [formChanged, setFormChanged] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    whoami().then((response) => {
      const stakeholder = response.data.stakeholder;
      setSchoolid(stakeholder.schoolid);
      setTeacherid(stakeholder.id);
      setTeacherEmail(response.data.email);

      const names = stakeholder.name.split(" ");
      if (names.length >= 2) {
        setFirstName(names[0]);
        setLastName(names.slice(1).join(" "));
      }

      const currentRoles = stakeholder.additionalRoles || [];
      setSelectedRoles(profilePageRedirect ? [] : currentRoles);
      const normalizedContactNumber = String(stakeholder.mobileNumber || "");
      setContactNumber(normalizedContactNumber);

      // Store initial values for change detection
      setInitialValues({
        firstName: names.length >= 2 ? names[0] : "",
        lastName: names.length >= 2 ? names.slice(1).join(" ") : "",
        contactNumber: normalizedContactNumber,
        selectedRoles: [...currentRoles],
      });
    });
  }, []);

  // Track form changes
  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      const changed =
        firstName !== initialValues.firstName ||
        lastName !== initialValues.lastName ||
        contactNumber !== initialValues.contactNumber ||
        !areArraysEqual(selectedRoles, initialValues.selectedRoles);
      setFormChanged(changed);
    }
  }, [firstName, lastName, contactNumber, selectedRoles, initialValues]);

  // Helper function to compare arrays
  const areArraysEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, idx) => val === sorted2[idx]);
  };

  useEffect(() => {
    getRoles()
      .then((res) => {
        const sortedRoles = res.data.sort((a, b) => {
          // First sort by sortOrder
          if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
          }
          // If sortOrder is the same, sort by name
          return a.name.localeCompare(b.name);
        });
        setRoleDefs(sortedRoles);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
     if (schoolid) {
       getCountryId(schoolid)
         .then((response) => {
           if (response.countryId) {
             console.log(response)
             setCountryId(response.countryId);
           } else {
             console.warn("No country ID found for school:", response.message);
             // Optionally set a default or leave as null
             setCountryId(null);
           }
         })
         .catch((error) => {
           console.error("Failed to fetch country ID:", error);
           // Optionally set a default value on error
           setCountryId(null);
         });
     }
   }, [schoolid]);

  function validateForm() {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!contactNumber.trim())
      newErrors.contactNumber = "Contact number is required";
    if (selectedRoles.length === 0)
      newErrors.selectedRoles = "At least one role is required";
    return newErrors;
  }

  function handleRoleChange(event) {
    const selectedRole = event.target.value;
    
    setSelectedRoles((prev) => {
      if (prev.includes(selectedRole)) {
        return prev.filter((role) => role !== selectedRole);
      } else {
        return [...prev, selectedRole];
      }
    });
  }

  function handleSubmitButtonClick() {
    // Show confirmation modal
    setShowConfirmModal(true);
  }

  function handleFinalSubmit() {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setShowConfirmModal(false);
      return;
    }

    const teacherData = {
      name: `${firstName} ${lastName}`,
      additionalRoles: selectedRoles,
      mobileNumber: contactNumber,
    };

    // Prepare objects to track what changed
    const oldValues = {};
    const newValues = {};

    // Check what fields changed and build our audit objects
    if (`${firstName} ${lastName}` !== `${initialValues.firstName} ${initialValues.lastName}`) {
      oldValues.name = `${initialValues.firstName} ${initialValues.lastName}`;
      newValues.name = `${firstName} ${lastName}`;
    }

    if (contactNumber !== initialValues.contactNumber) {
      oldValues.mobileNumber = initialValues.contactNumber;
      newValues.mobileNumber = contactNumber;
    }

    if (!areArraysEqual(selectedRoles, initialValues.selectedRoles)) {
      oldValues.additionalRoles = initialValues.selectedRoles;
      newValues.additionalRoles = selectedRoles;
    }

    updateTeacherProfile(schoolid, teacherid, teacherData)
      .then((response) => {
        // Mark this teacher as caught up on role selection, then refresh the
        // shared context so the route gate doesn't immediately bounce them
        // back to /profile when we navigate away.
        const finishUp = () =>
          postLastLogin(teacherid)
            .catch((error) => {
              console.error("Error recording last login after profile update:", error);
            })
            .finally(() => {
              refreshRoleSelection().finally(() => navigate(-1));
            });

        // Only log if something actually changed
        if (Object.keys(newValues).length > 0) {
          // Log the teacher update
          logTeacherUpdate(teacherid, oldValues, newValues)
            .catch((error) => {
              console.error("Error logging teacher update:", error);
              // Still proceed even if logging fails
            })
            .finally(finishUp);
        } else {
          // No changes to log, just proceed
          finishUp();
        }
      })
      .catch((error) => {
        console.error("Error updating teacher:", error);
        setShowConfirmModal(false);
      });
  }

  const hasContactNumber = contactNumber.trim().length > 0;

  return (
    <>
      <Form
        className="profile-form-detail form-teacher-profile"
        onSubmit={(e) => e.preventDefault()}
      >
        {profilePageRedirect ? (
          <h2 className="profile-page-description-header">
            Select all roles that apply to you for the coming school year.
          </h2>
        ) : (
          <h2 className="profile-page-description-header">Edit Your Profile</h2>
        )}

        {/* Important notice for role selection */}
        {profilePageRedirect && (
          <div className="text-center">
            <div className="important-notice">
              <div className="notice-content">
                <h4>Important</h4>
                <p>
                  Roles from last school year are not carried forward. Select every
                  role that currently applies to ensure continued access.
                </p>
              </div>
            </div>
          </div>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert variant="danger">
            Please correct the following errors:
            <ul>
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Form.Group as={Row} controlId="firstname" className="mb-3">
          <Form.Label column sm={2} className="profile-page-label">
            First Name
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              className={`form-control ${firstName !== initialValues.firstName ? 'changed' : ''}`}
              type="text"
              placeholder={firstName}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="lastname" className="mb-3">
          <Form.Label column sm={2} className="profile-page-label">
            Last Name
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              className={`form-control ${lastName !== initialValues.lastName ? 'changed' : ''}`}
              type="text"
              placeholder={lastName}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="contactNumber" className="mb-5">
          <Form.Label column sm={2} className="profile-page-label">
            Contact Number{" "}
            <span className="text-danger" aria-hidden="true">
              *
            </span>
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              className={`form-control ${contactNumber !== initialValues.contactNumber ? 'changed' : ''}`}
              type="tel"
              placeholder={contactNumber || "Enter contact number"}
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              required
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-5">
          <Form.Label column sm={2} className="profile-page-label">
            Select your Roles:
          </Form.Label>
          <Col sm={10} className="grid-control">
            <div className="checkbox-grid">
              {roleDefs
                .filter((role) => role.countryid === countryid)
                .map((role) => (
                  <Form.Check
                    key={role.id}
                    type="checkbox"
                    id={`role-${role.id}`}
                    label={role.name}
                    value={role.name}
                    onChange={handleRoleChange}
                    checked={selectedRoles.includes(role.name)}
                    className={selectedRoles.includes(role.name) && 
                      initialValues.selectedRoles && !initialValues.selectedRoles.includes(role.name) 
                      ? 'changed-role' : ''}
                  />
                ))}
            </div>
          </Col>
        </Form.Group>
        {/* Buttons Section */}
        <div className="button-group-edit-profile">
          <SimpleNavigationButton path={-1} label="Cancel" colour="white" />
          <button
            className="green-button"
            type="button"
            onClick={handleSubmitButtonClick}
            disabled={!hasContactNumber}
          >
            {formChanged ? "Update" : "Confirm"}
          </button>
        </div>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        className="teacher-profile-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{formChanged ? "Update Your Details" : "Confirm Your Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-details">
            <div className="detail-row">
              <div className="detail-label">Name:</div>
              <div className="detail-value">{firstName} {lastName}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Contact Number:</div>
              <div className="detail-value">{contactNumber}</div>
            </div>
            <div className="detail-row roles-section">
              <div className="detail-label">Selected Roles:</div>
              <div className="detail-value">
                <ul className="role-list">
                  {selectedRoles.map(role => (
                    <li key={role}>{role}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="confirmation-question">
            Are these details correct for the {formatAcademicYear(currentAcademicYear, "fullDisplay")} school year?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="cancel-button" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </button>
          <button className="confirm-button" onClick={handleFinalSubmit}>
            {formChanged ? "Update" : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TeacherProfileForm;
